import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  TextField,
  Typography,
  Grid,
  Alert,
  AlertTitle,
  MenuItem,
  Card,
  CardContent,
  Chip,
  Button,
  IconButton,
  Stack,
  Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import HistoryIcon from '@mui/icons-material/History';
import {
  MESES_PADRAO,
  MESES_MINIMO,
  MESES_MAXIMO,
  ATALHOS_DE_MESES,
  GRUPOS,
  limitarQuantidadeDeMeses,
  rotulosMeses
} from '../../config/planoContas';
import {
  competenciaDe,
  janelaPermitida,
  listarEmFrase,
  tetoDeMeses
} from '../../services/mesesPermitidos';
import QuestionarioDePerfil from '../QuestionarioDePerfil';
import { listarPlanos } from '../../services/planoService';
import { listarPeriodos } from '../../services/periodoService';

const NOMES_MES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

/**
 * O tempo de preenchimento, em minutos.
 *
 * Era um mapa com as chaves 3, 6 e 12 — e virou promessa vazia no dia em que a
 * quantidade passou a ser digitada livre: quem escolhesse 7 lia "alguns
 * minutos". A conta é grosseira de propósito (um mês custa uns 4 minutos, mais
 * um pedaço fixo de leitura), mas responde para qualquer número. Prometer o
 * tempo de três meses para quem escolheu doze é o tipo de promessa que faz o
 * usuário abandonar no meio.
 */
export const tempoEstimado = (n) => {
  const minutos = Math.max(5, Math.round((6 + Number(n) * 3.5) / 5) * 5);
  return `cerca de ${minutos} minutos`;
};

/**
 * O plano de quem está preenchendo, para a régua de meses.
 *
 * Duas rotas porque o produto é link público: `/api/planos` responde a
 * visitante anônimo e traz o catálogo (é de lá que sai o limite — escrever um
 * `3` aqui criaria uma segunda lista do mesmo catálogo, que diverge no dia em
 * que alguém mexer no plano). `/api/periodos` só existe para quem tem conta, e
 * é ela que diz quantos meses a empresa JÁ lançou.
 *
 * Falha de rede cai no gratuito, nunca no ilimitado: liberar 12 meses por erro
 * de rede é coletar 384 campos que o backend depois recusa.
 */
const useSituacaoDoPlano = () => {
  const [situacao, setSituacao] = useState({
    carregando: true,
    limiteCompetencias: 3,
    nomeDoPlano: 'Gratuito',
    competenciasJaLancadas: []
  });

  useEffect(() => {
    let vivo = true;

    const carregar = async () => {
      try {
        const catalogo = await listarPlanos();
        const atual = (catalogo.planos || []).find((p) => p.id === catalogo.atual);
        const base = {
          carregando: false,
          limiteCompetencias: atual ? atual.limiteCompetencias : 3,
          nomeDoPlano: atual ? atual.nome : 'Gratuito',
          competenciasJaLancadas: []
        };

        if (!catalogo.autenticado) {
          if (vivo) setSituacao(base);
          return;
        }

        const periodos = await listarPeriodos();
        if (!vivo) return;
        setSituacao({
          ...base,
          limiteCompetencias: periodos.plano?.limiteCompetencias ?? base.limiteCompetencias,
          nomeDoPlano: periodos.plano?.nome || base.nomeDoPlano,
          competenciasJaLancadas: periodos.cobertura?.competencias || []
        });
      } catch (erro) {
        if (vivo) setSituacao((s) => ({ ...s, carregando: false }));
      }
    };

    carregar();
    return () => {
      vivo = false;
    };
  }, []);

  return situacao;
};

/**
 * @param {Function} aoSaberDoPlano — sobe o plano e o veredito da janela para o
 *   Wizard. Sem isto o botão "Próximo" deixaria o usuário coletar 24 páginas de
 *   formulário que o plano recusa a gravar, e a revisão final não teria como
 *   dizer quais meses já existem — o dado do plano só é buscado aqui.
 */
const StepConfig = ({
  config, onChange, aoVerPlanos = null, aoSaberDoPlano = null,
  perfil = null, aoMudarPerfil = null, catalogo = GRUPOS
}) => {
  const totalDeContas = catalogo.reduce((soma, g) => soma + g.contas.length, 0);
  const anoAtual = new Date().getFullYear();
  const anos = [anoAtual, anoAtual - 1, anoAtual - 2];
  const quantidade = Number(config.quantidadeMeses) || MESES_PADRAO;
  const meses = rotulosMeses(config.mes, config.ano, quantidade);
  const plano = useSituacaoDoPlano();

  // O campo tem estado próprio porque ele aceita passar por "vazio" enquanto a
  // pessoa apaga o 3 para digitar 12. Empurrar cada tecla direto para o
  // `config` faria a janela virar 1 mês no instante em que o campo ficasse em
  // branco — e o realinhamento do Wizard jogaria fora os valores digitados.
  const [textoQuantidade, setTextoQuantidade] = useState(String(quantidade));
  useEffect(() => setTextoQuantidade(String(quantidade)), [quantidade]);

  // Recalcula quando o usuário troca mês, ano ou quantidade: a janela muda, as
  // competências novas mudam, e o que cabe no plano muda junto.
  const escolhida = useMemo(
    () =>
      janelaPermitida({
        n: quantidade,
        limiteCompetencias: plano.limiteCompetencias,
        nomeDoPlano: plano.nomeDoPlano,
        competenciasJaLancadas: plano.competenciasJaLancadas,
        mesReferencia: config.mes,
        anoReferencia: config.ano
      }),
    [plano, config.mes, config.ano, quantidade]
  );

  const atalhos = useMemo(
    () =>
      ATALHOS_DE_MESES.map((n) => ({
        n,
        liberado: plano.limiteCompetencias === null || n <= plano.limiteCompetencias
      })),
    [plano.limiteCompetencias]
  );

  const teto = tetoDeMeses(plano.limiteCompetencias);

  useEffect(() => {
    if (!aoSaberDoPlano || plano.carregando) return;
    aoSaberDoPlano({
      limiteCompetencias: plano.limiteCompetencias,
      nomeDoPlano: plano.nomeDoPlano,
      competenciasJaLancadas: plano.competenciasJaLancadas,
      janelaLiberada: escolhida.liberado
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plano, escolhida.liberado]);

  const set = (campo, valor) => onChange({ ...config, [campo]: valor });

  const definirQuantidade = (valor) => {
    const limitada = limitarQuantidadeDeMeses(valor);
    setTextoQuantidade(String(limitada));
    if (limitada !== quantidade) set('quantidadeMeses', limitada);
  };

  /**
   * Rascunho gravado com 12 meses, aberto depois numa conta gratuita, ficaria
   * preso num passo que o botão não deixa sair. Cair para o teto do plano é o
   * único desfecho que não trava o usuário.
   *
   * Roda UMA vez, quando o plano termina de carregar — e não a cada vez que a
   * janela fica bloqueada. Enquanto era "sempre que `liberado` for falso", quem
   * digitasse 7 num plano de 3 via o número saltar de volta para 3 no meio da
   * digitação. Número escolhido acima do plano continua na tela, bloqueado e
   * com o caminho para assinar ao lado; quem troca é o usuário.
   */
  const [ajustouRascunho, setAjustouRascunho] = useState(false);
  useEffect(() => {
    if (plano.carregando || ajustouRascunho) return;
    setAjustouRascunho(true);
    if (quantidade > teto) set('quantidadeMeses', teto);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plano.carregando]);

  return (
    <Box>
      <Box
        sx={{
          mb: 3,
          p: 2,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          borderRadius: 1
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Vamos montar o DRE da sua empresa
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.95 }}>
          {catalogo.length} grupos de perguntas, {totalDeContas} contas, {quantidade}{' '}
          {quantidade === 1 ? 'mês' : 'meses'}. Leva {tempoEstimado(quantidade)}
          {quantidade > 1 ? ' — a partir do segundo mês, o que não muda se repete em um clique.' : '.'}
        </Typography>
      </Box>

      {/* O PERFIL DECIDE QUAIS PERGUNTAS VÊM DEPOIS.
          Fica recolhido: quem não quiser responder nada segue com o recorte
          padrão, que já produz DRE, margem e ponto de equilíbrio. */}
      {aoMudarPerfil && (
        <Accordion sx={{ mb: 3 }} disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Ajustar as perguntas ao seu negócio
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Opcional. Responda e o questionário passa a pedir só o que a sua empresa tem.
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <QuestionarioDePerfil
              respostas={perfil || undefined}
              onChange={aoMudarPerfil}
              prefixo="Seu questionário vai pedir"
              substantivo="contas"
              contagemBase={totalDeContas}
            />
          </AccordionDetails>
        </Accordion>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        Escolha o <strong>último mês</strong> da janela — aquele em que você já sabe todos os
        valores.{' '}
        {quantidade === 1 && 'As perguntas vão cobrir esse mês. '}
        {quantidade === 2 && 'As perguntas vão cobrir esse mês e o anterior. '}
        {quantidade > 2 && `As perguntas vão cobrir esse mês e os ${quantidade - 1} anteriores. `}
        Você não precisa saber todos: <strong>mês que ficar em branco fica de fora</strong>, e o
        produto nunca conta lacuna como zero.
      </Alert>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Nome da empresa"
            placeholder="Como você quer ver no relatório"
            value={config.empresa || ''}
            onChange={(e) => set('empresa', e.target.value)}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            label="Último mês da janela"
            value={config.mes}
            onChange={(e) => set('mes', Number(e.target.value))}
          >
            {NOMES_MES.map((nome, i) => (
              <MenuItem key={nome} value={i + 1}>
                {nome}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            label="Ano"
            value={config.ano}
            onChange={(e) => set('ano', Number(e.target.value))}
          >
            {anos.map((ano) => (
              <MenuItem key={ano} value={ano}>
                {ano}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      <Card variant="outlined" sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Quantos meses você quer levantar?
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            Qualquer número de {MESES_MINIMO} a {MESES_MAXIMO}. Quanto mais meses, mais o produto
            enxerga tendência em vez de fotografia.
          </Typography>

          {/* Campo digitável com os atalhos ao lado, e não uma lista fechada de
              opções: quem precisa de sete meses precisa PEDIR sete. Os atalhos
              cobrem a escolha comum em um toque; o bloqueado continua visível
              com o cadeado, porque opção escondida não faz upsell — ela só faz
              o produto parecer menor. */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ flexWrap: 'wrap', rowGap: 1 }}
          >
            <IconButton
              aria-label="Um mês a menos"
              onClick={() => definirQuantidade(quantidade - 1)}
              disabled={quantidade <= MESES_MINIMO}
              sx={{ border: 1, borderColor: 'divider', borderRadius: 1, width: 44, height: 44 }}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>

            <TextField
              label="Meses"
              value={textoQuantidade}
              onChange={(e) => setTextoQuantidade(e.target.value.replace(/[^\d]/g, ''))}
              onBlur={() => definirQuantidade(textoQuantidade)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') definirQuantidade(textoQuantidade);
              }}
              inputProps={{
                inputMode: 'numeric',
                'aria-label': 'Quantidade de meses',
                style: { textAlign: 'center', width: 64 }
              }}
              size="small"
            />

            <IconButton
              aria-label="Um mês a mais"
              onClick={() => definirQuantidade(quantidade + 1)}
              disabled={quantidade >= MESES_MAXIMO}
              sx={{ border: 1, borderColor: 'divider', borderRadius: 1, width: 44, height: 44 }}
            >
              <AddIcon fontSize="small" />
            </IconButton>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: { sm: 1 } }}>
              {atalhos.map((atalho) => (
                <Tooltip
                  key={atalho.n}
                  title={atalho.liberado ? '' : `Exige plano pago`}
                  placement="top"
                >
                  <span>
                    <Chip
                      label={`${atalho.n} meses`}
                      icon={atalho.liberado ? undefined : <LockOutlinedIcon fontSize="small" />}
                      onClick={atalho.liberado ? () => definirQuantidade(atalho.n) : undefined}
                      disabled={!atalho.liberado}
                      color={atalho.n === quantidade ? 'primary' : 'default'}
                      variant={atalho.n === quantidade ? 'filled' : 'outlined'}
                      sx={{ height: 36 }}
                    />
                  </span>
                </Tooltip>
              ))}
            </Box>
          </Stack>

          {!escolhida.liberado && (
            <Alert
              severity="info"
              sx={{ mt: 2 }}
              action={
                aoVerPlanos ? (
                  <Button
                    color="inherit"
                    size="small"
                    sx={{ minHeight: 44 }}
                    onClick={() => aoVerPlanos(null)}
                  >
                    Ver os planos
                  </Button>
                ) : null
              }
            >
              <AlertTitle>
                Acima de {teto} {teto === 1 ? 'mês' : 'meses'} exige plano pago
              </AlertTitle>
              {escolhida.motivo}
            </Alert>
          )}

          {escolhida.aviso && (
            <Alert
              severity="warning"
              sx={{ mt: 2 }}
              action={
                aoVerPlanos ? (
                  <Button
                    color="inherit"
                    size="small"
                    sx={{ minHeight: 44 }}
                    onClick={() => aoVerPlanos({ bloqueados: escolhida.novas })}
                  >
                    Ver os planos
                  </Button>
                ) : null
              }
            >
              {escolhida.aviso}
            </Alert>
          )}

          {/* A sobreposição é dita AQUI, na hora da escolha, e repetida na
              revisão. O dado já estava carregado e servia só para a aritmética
              de cota: a tela sabia que dezembro estava lançado e não dizia uma
              palavra antes de substituí-lo. */}
          {escolhida.existentesRotulos.length > 0 && (
            <Alert severity="info" icon={<HistoryIcon />} sx={{ mt: 2 }}>
              <AlertTitle>
                {listarEmFrase(escolhida.existentesRotulos)}{' '}
                {escolhida.existentesRotulos.length === 1 ? 'já está lançado' : 'já estão lançados'}
              </AlertTitle>
              O que você preencher aqui substitui o que está guardado{' '}
              {escolhida.existentesRotulos.length === 1 ? 'nesse mês' : 'nesses meses'} — a versão
              anterior continua no histórico. Corrigir mês seu nunca consome o limite do plano.
              Deixe o mês em branco para não mexer nele.
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mt: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <CalendarMonthIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2" fontWeight="bold">
              Você vai informar valores destes meses
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {meses.map((mes) => {
              const jaLancado = escolhida.existentes.includes(competenciaDe(mes));
              return (
                <Chip
                  key={mes.campo}
                  label={jaLancado ? `${mes.longo} · já lançado` : mes.longo}
                  color={jaLancado ? 'default' : 'primary'}
                  variant="outlined"
                  icon={jaLancado ? <HistoryIcon fontSize="small" /> : undefined}
                />
              );
            })}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default StepConfig;
